import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Box, Button, Typography, Paper } from '@mui/material'
import ROUTES from '../../routesConfig'

const extractAppSlug = (app) => {
  const attrs = app?.app?.attributes || {}
  return attrs.slug || attrs['slug'] || app?.slug || app?.app?.slug || null
}

const RibbonItem = ({ app, buildState }) => {
  const location = useLocation()
  const attrs = app.app.attributes
  const iconUrl = attrs['icon-url']
  const appSlug = extractAppSlug(app)
  const launchFrom = `${location.pathname}${location.search || ''}`
  const playableApp = buildState(app)
  const createEventTarget = appSlug || playableApp.zoneId

  return (
    <Link
      to={ROUTES.CREATE_EVENT(createEventTarget)}
      state={{ playableApp, launchFrom }}
      style={{ textDecoration: 'none', display: 'block', width: '100%', minWidth: 0 }}
    >
      <Paper
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.08)',
          borderRadius: 2,
          px: { xs: 1.25, sm: 1.5 },
          py: { xs: 1.5, sm: 2.5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1.25, sm: 2 },
          minWidth: 0,
          width: '100%',
          boxSizing: 'border-box',
          border: '1px solid rgba(255,255,255,0.1)',
          transition: 'background 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.12)',
            borderColor: 'rgba(255,255,255,0.15)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 2 }, minWidth: 0, flex: 1 }}>
          <Box
            component="img"
            src={iconUrl}
            alt={attrs['short-name']}
            loading="lazy"
            sx={{
              width: { xs: 64, sm: 80 },
              height: { xs: 64, sm: 80 },
              borderRadius: 2,
              objectFit: 'cover',
              boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
              flexShrink: 0
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="h5"
              noWrap
              sx={{
                color: '#E6E9EF',
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.4rem' },
                lineHeight: 1.2
              }}
            >
              {attrs['short-name']}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(230,233,239,0.7)',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                lineHeight: 1.35
              }}
            >
              {attrs['long-name']}
            </Typography>
          </Box>
        </Box>
        <Button
          size="small"
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999,
            px: { xs: 2.1, sm: 3 },
            py: { xs: 0.7, sm: 1 },
            minWidth: { xs: '4.5rem', sm: '5.25rem' },
            flexShrink: 0,
            fontSize: { xs: '0.9rem', sm: '0.95rem' },
            background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)',
              boxShadow: '0 6px 16px rgba(34, 197, 94, 0.4)',
              transform: 'translateY(-1px)'
            },
            transition: 'all 160ms ease'
          }}
        >
          Play
        </Button>
      </Paper>
    </Link>
  )
}

const RibbonAppList = ({ apps, buildState, footer = null }) => {
  return (
    <Box sx={{
      bgcolor: '#0B0F19',
      minHeight: 'calc(100svh - 80px)',
      height: 'calc(100svh - 80px)',
      borderRadius: 2,
      p: { xs: 1, sm: 1.5 },
      overflowX: 'hidden',
      overflowY: 'auto',
      width: '100%',
      boxSizing: 'border-box',
      maxWidth: '100%'
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', minWidth: 0 }}>
        {apps.map(app => (
          <RibbonItem key={app.app.id} app={app} buildState={buildState} />
        ))}
        {footer}
      </Box>
    </Box>
  )
}

export default RibbonAppList
